import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useAppTranslation } from '../../hooks'
import { getAppBarState } from 'src/store'
import { CustomAutocomplete } from './CustomAutoComplete'
import { fetchProductOnlineCheck } from '../../services/financial-flows/productOnlineCheck'
import { ProductOnlineCheckItem } from '../../services/financial-flows/types'
import { CircularProgress, Box, Typography } from '@mui/material'

export interface ProductOnlineCheckOption {
  value: number
  label: string
  description: string
}

export interface ProductOnlineCheckSelectProps {
  disabled?: boolean
  value: ProductOnlineCheckOption | null
  onChange?: (value: ProductOnlineCheckOption | null) => void
  getDefaultValue?: (value: ProductOnlineCheckOption | null) => void
  disableClearable?: boolean
  selectDefaultProduct?: boolean
}

export const ProductOnlineCheckSelect: React.FC<ProductOnlineCheckSelectProps> = ({
  disabled = false,
  onChange,
  value,
  getDefaultValue,
  disableClearable = false,
  selectDefaultProduct = true,
}) => {
  const { label } = useAppTranslation()
  const { user } = getAppBarState().appState
  const [products, setProducts] = useState<ProductOnlineCheckItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch products from API
  const loadProducts = useCallback(async () => {
    if (!user?.profile?.sub) {
      setError('No se encontró el ID del usuario')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetchProductOnlineCheck(user.profile.sub)
      setProducts(response.data)
    } catch (err) {
      console.error('Error loading products:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar los productos')
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [user?.profile?.sub])

  // Load products on mount
  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  // Create options array with value and label
  const productOptions = useMemo<ProductOnlineCheckOption[]>(() => {
    return products.map(product => ({
      value: product.Id,
      label: product.Name,
      description: product.Description,
    }))
  }, [products])

  // Auto-select first product when products are available and no value is set
  useEffect(() => {
    if (selectDefaultProduct && productOptions.length > 0 && !value && getDefaultValue) {
      const firstProduct = productOptions[0]
      getDefaultValue(firstProduct)
    }
  }, [selectDefaultProduct, productOptions, value, getDefaultValue])

  // Find current selected option based on external value
  const selectedOption = useMemo(() => {
    if (!value) return null
    return productOptions.find(option => option.value === value.value) || value
  }, [value, productOptions])

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="56px">
        <CircularProgress size={24} />
      </Box>
    )
  }

  // Show error state
  if (error) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="56px">
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    )
  }

  return (
    <CustomAutocomplete
      data={productOptions}
      value={selectedOption}
      onChangeValue={(selectedOption) => {
        if (onChange) {
          onChange(selectedOption || null)
        }
      }}
      placeholder={label('PRODUCTO')}
      keyLabel="label"
      label={label('PRODUCTO')}
      disableClearable={disableClearable}
      disabled={disabled}
    />
  )
}

export default ProductOnlineCheckSelect
